<script setup>
  import { ref, computed, inject } from 'vue'
  import BaseOffCanvas from '../common/BaseOffCanvas.vue'
  import BcstModuleProgressHeader from './BcstModuleProgressHeader.vue'
  import BcsStatPill from '../common/BcsStatPill.vue'
  import { useTransformLearningStore } from '../../stores/TransformLearningStore'
  import checkGreenIcon from '@/assets/images/bcst-check-green.svg'
  import checkGrayIcon from '@/assets/images/bcst-check-gray.svg'
  import lockQuizzIcon from '@/assets/images/bcst-lock-quizz.svg'
  import failedQuizIcon from '@/assets/images/bcst-failed-quiz.svg'
  import learningIcon from '@/assets/images/bcst-learning-dashboard.svg'

  /*
   * Rendered as-is for the authenticated user (no props). The Transform Tracker reuses it
   * to show another user's progress through the admin view-as context, and passes that
   * user's name so the admin can see whose progress is on screen.
   */
  const props = defineProps({
    ownerName: {
      type: String,
      default: ''
    },
    /** Set by pages rendered without the navbar, so the panel spans the whole viewport. */
    fullHeight: {
      type: Boolean,
      default: false
    },
    /*
     * Read-only lesson list: rows do not navigate to the lesson. Set by the Admin Center, whose
     * admins may not have access to the Transform pages, and where closing the panel drops the
     * view-as context the lesson page would need.
     */
    disableLessonNavigation: {
      type: Boolean,
      default: false
    }
  })

  // Forwarded from BaseOffCanvas: the tracker leaves the view-as context on close, which
  // has to happen however the panel was dismissed (button, backdrop or Escape).
  const emit = defineEmits(['hide'])

  const offcanvasRef = ref(null)
  const store = useTransformLearningStore()
  const customNavigation = inject('customNavigation')

  const expandedModuleIds = ref(new Set())

  async function open() {
    /*
     * The expanded set is REBUILT on every open, never added to. The Transform Tracker
     * reuses this panel for one user after another, and a module left expanded for the
     * previous target would still be open for the next one.
     *
     * It expands the module the user has to continue in — and nothing at all once the
     * course is fully completed, since there is no lesson left to point at.
     */
    const targetModuleId = store.continueLesson?.module?.id
    expandedModuleIds.value = targetModuleId ? new Set([targetModuleId]) : new Set()

    await offcanvasRef.value?.show()

    // Quiz question counts (and assessment data) load lazily per module. The dashboard
    // only preloads the Continue Card's module, so every other quiz would render
    // "0 Questions". Load the rest so counts are real; cached modules return immediately
    // and the enriched meta updates reactively as each resolves.
    await Promise.all(store.modules.map(m => store.loadCategoryDetail(m.id)))
  }

  async function close() {
    await offcanvasRef.value?.hide()
  }

  function toggleModule(moduleId) {
    if (expandedModuleIds.value.has(moduleId)) {
      expandedModuleIds.value.delete(moduleId)
    } else {
      expandedModuleIds.value.add(moduleId)
    }
    expandedModuleIds.value = new Set(expandedModuleIds.value)
  }

  function isModuleExpanded(moduleId) {
    return expandedModuleIds.value.has(moduleId)
  }

  const sortedModules = computed(() => {
    return [...store.modules].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  })

  const modulesWithProgress = computed(() => {
    return sortedModules.value.map(module => {
      const detail = store.categoryDetail(module.id)
      return {
        ...module,
        lessons: detail?.lessons ?? [],
        total: detail?.total ?? 0,
        completedCount: detail?.completedCount ?? 0,
        percent: detail?.percent ?? 0,
      }
    })
  })

  const DONE_STATUSES = ['watched', 'passed']
  function isDone(lesson) {
    return DONE_STATUSES.includes(lesson.status)
  }

  function isLocked(lesson) {
    return lesson.lessonType === 'quiz' && lesson.quiz?.locked === true
  }

  function isInProgress(lesson) {
    return lesson.status === 'in_progress'
  }

  function isFailed(lesson) {
    return lesson.lessonType === 'quiz' && lesson.status === 'failed'
  }

  function lessonMeta(lesson) {
    if (lesson.lessonType === 'video') {
      return lesson.durationLabel ? `Video Lesson, ${lesson.durationLabel}` : 'Video Lesson'
    }
    if (lesson.lessonType === 'quiz') {
      const q = lesson.quiz?.questions ?? 0
      return `Quiz, ${q} Question${q !== 1 ? 's' : ''}`
    }
    return ''
  }

  function lessonIcon(lesson) {
    if (isDone(lesson)) return checkGreenIcon
    if (isLocked(lesson)) return lockQuizzIcon
    if (isFailed(lesson)) return failedQuizIcon
    if (isInProgress(lesson)) return null // Use Bootstrap icon instead
    return checkGrayIcon
  }

  function lessonIconClass(lesson) {
    if (isDone(lesson)) return 'bcpp-item-icon--done'
    if (isLocked(lesson)) return 'bcpp-item-icon--locked'
    if (isInProgress(lesson)) return 'bcpp-item-icon--in-progress'
    if (isFailed(lesson)) return 'bcpp-item-icon--failed'
    return 'bcpp-item-icon--not-started'
  }

  function navigateToLesson(lesson) {
    if (props.disableLessonNavigation || !lesson?.id || isLocked(lesson)) return
    customNavigation.value?.navigate({
      newPage: customNavigation.value.routes.transformLessonDetail.name,
      params: {
        lessonId: lesson.id,
        lessonTitle: lesson.title ?? '',
        moduleId: lesson.moduleId,
      }
    })
    close()
  }

  // The prerequisite lesson a locked quiz points to. Resolved within the same module
  // (the video↔quiz link is positional and never crosses modules), so the "Lesson N"
  // number matches the module's own presentation numbering (displayNumber).
  function requiredLesson(module, lesson) {
    const reqId = lesson.quiz?.requiredLessonId
    if (!reqId) return null
    return module.lessons.find(l => l.id === reqId) ?? null
  }

  function requiredLessonDisplayIndex(module, lesson) {
    return requiredLesson(module, lesson)?.displayNumber ?? null
  }

  function navigateToRequiredLesson(module, lesson) {
    const req = requiredLesson(module, lesson)
    if (req) navigateToLesson(req)
  }

  // All enriched lessons (with status) across every module — the single source the
  // actionable progress metrics below are derived from.
  const allLessons = computed(() =>
    modulesWithProgress.value.flatMap(module => module.lessons)
  )

  // "Videos Completed" — X/Y fraction of video lessons whose status is 'watched'. Videos and
  // quizzes are separate stats; the header's "X of 48 Lessons" is the two of them together.
  const videosCompletedLabel = computed(() => {
    const videos = allLessons.value.filter(l => l.lessonType === 'video')
    const watched = videos.filter(l => l.status === 'watched').length
    return `${watched}/${videos.length}`
  })

  // "Quizzes Passed" — X/Y fraction of quiz lessons whose status is 'passed'.
  const quizzesPassedLabel = computed(() => {
    const quizzes = allLessons.value.filter(l => l.lessonType === 'quiz')
    const passed = quizzes.filter(l => l.status === 'passed').length
    return `${passed}/${quizzes.length}`
  })

  // "Time Remaining" — total duration of video lessons not yet watched.
  const timeRemaining = computed(() => {
    const totalSeconds = allLessons.value.reduce((sum, lesson) => {
      if (lesson.lessonType === 'video' && lesson.status !== 'watched' && lesson.videoDurationSeconds) {
        return sum + lesson.videoDurationSeconds
      }
      return sum
    }, 0)

    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.round((totalSeconds % 3600) / 60)

    if (hours > 0) {
      return `${hours}h+`
    }
    return `${minutes}m`
  })

  defineExpose({ open, close })
</script>

<template>
  <BaseOffCanvas
    ref="offcanvasRef"
    canvas-id="bcs-course-progress-panel"
    canvas-position="end"
    width="350px"
    :full-height="props.fullHeight"
    @hide="emit('hide')">

    <template #title>
      <span class="bcpp-title">View Progress Details</span>
      <span v-if="props.ownerName" class="bcpp-owner">{{ props.ownerName }}</span>
    </template>

    <template #default>
      <div class="bcpp-stats">
        <BcsStatPill
          :value="videosCompletedLabel"
          label="Videos Completed"
          size="md" />
        <BcsStatPill
          :value="timeRemaining"
          label="Time Remaining"
          size="md" />
        <BcsStatPill
          :value="quizzesPassedLabel"
          label="Quizzes Passed"
          size="md" />
      </div>

      <BcstModuleProgressHeader
        title="Course Progress Overview"
        :total="store.totalLessons"
        :completedCount="store.completedLessons"
        :percent="store.overallPercent"
        labelTooltip="Each video and each quiz counts as one lesson" />

      <div class="bcpp-modules">
        <div
          v-for="module in modulesWithProgress"
          :key="module.id"
          class="bcpp-module">
          <button
            type="button"
            class="bcpp-module-header"
            @click="toggleModule(module.id)">
            <div class="bcpp-module-icon">
              <img :src="learningIcon" alt="" class="bcpp-icon-img" />
            </div>
            <div class="bcpp-module-header-content">
              <h3 class="bcpp-module-title">{{ module.title }}</h3>
              <p class="bcpp-module-summary">
                {{ module.completedCount }} of {{ module.total }} Lessons completed
              </p>
            </div>
            <i
              class="bi bcpp-module-chevron"
              :class="isModuleExpanded(module.id) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
          </button>

          <div
            v-if="isModuleExpanded(module.id)"
            class="bcpp-module-lessons">
            <template v-for="lesson in module.lessons" :key="lesson.id">
              <!-- Locked quiz: non-interactive container holding the real unlock link.
                   A disabled <button> would swallow clicks to the "Lesson N" link, and a
                   <button> may not nest another interactive control — mirrors TransformLessonSidebar. -->
              <div
                v-if="isLocked(lesson)"
                class="bcpp-item bcpp-item--locked"
                aria-disabled="true">
                <span class="bcpp-item-status">
                  <img
                    :src="lockQuizzIcon"
                    alt=""
                    class="bcpp-item-icon bcpp-item-icon--locked" />
                </span>
                <span class="bcpp-item-body">
                  <span class="bcpp-item-title">{{ lesson.title }}</span>
                  <span class="bcpp-item-meta">{{ lessonMeta(lesson) }}</span>
                  <span class="bcpp-item-locked-text">
                    Complete
                    <span v-if="props.disableLessonNavigation">
                      Lesson {{ requiredLessonDisplayIndex(module, lesson) }}
                    </span>
                    <button
                      v-else
                      type="button"
                      class="bcpp-item-locked-link"
                      @click.stop="navigateToRequiredLesson(module, lesson)">
                      Lesson {{ requiredLessonDisplayIndex(module, lesson) }}
                    </button>
                    to unlock quiz.
                  </span>
                </span>
              </div>
              <button
                v-else
                type="button"
                class="bcpp-item"
                :class="props.disableLessonNavigation ? 'bcpp-item--static' : 'bcpp-item--clickable'"
                :disabled="props.disableLessonNavigation"
                @click="navigateToLesson(lesson)">
                <span class="bcpp-item-status">
                  <span
                    v-if="isInProgress(lesson)"
                    class="bcpp-item-icon bcpp-item-icon--in-progress">
                    <i class="bi bi-clock"></i>
                  </span>
                  <img
                    v-else
                    :src="lessonIcon(lesson)"
                    alt=""
                    :class="['bcpp-item-icon', lessonIconClass(lesson)]" />
                </span>
                <span class="bcpp-item-body">
                  <span class="bcpp-item-title">{{ lesson.title }}</span>
                  <span class="bcpp-item-meta">{{ lessonMeta(lesson) }}</span>
                </span>
              </button>
            </template>
          </div>
        </div>
      </div>
    </template>
  </BaseOffCanvas>
</template>

<style scoped lang="scss">
  /* Force the offcanvas width override */
  :deep(.offcanvas) {
    width: 350px !important;
    max-width: 350px !important;
  }

  .bcpp-title {
    font-size: 1.375rem;
    font-weight: 700;
    line-height: 125%;
    letter-spacing: -0.44px;
    color: var(--bs-body-color);
  }

  /* Whose progress this is — only rendered when a consumer passes ownerName. */
  .bcpp-owner {
    display: block;
    font-size: 14px;
    font-weight: 400;
    color: var(--bs-gray-700);
  }

  .bcpp-stats {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--bs-gray-300);
  }

  .bcpp-modules {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .bcpp-module {
    border: 1px solid var(--bs-gray-300);
    border-radius: 4px;
    overflow: hidden;
  }

  .bcpp-module-header {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    background: #f4f4f5;
    border: none;
    padding: 16px 20px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: var(--bs-gray-200);
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--bs-primary);
      outline-offset: -2px;
    }
  }

  .bcpp-module-icon {
    background-color: var(--bs-white);
    width: 50px;
    height: 50px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .bcpp-icon-img {
    width: 28.814px;
    height: 28.814px;
  }

  .bcpp-module-header-content {
    flex: 1;
    min-width: 0;
  }

  .bcpp-module-title {
    color: var(--bs-gray-700);
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
    margin: 0 0 4px 0;
    line-height: 1.3;
  }

  .bcpp-module-summary {
    color: var(--bs-body-color);
    font-size: 14px;
    letter-spacing: -0.28px;
    margin: 0;
    line-height: 1.3;
  }

  .bcpp-module-chevron {
    flex-shrink: 0;
    font-size: 16px;
    color: var(--bs-body-color);
    margin-left: 12px;
    transition: transform 0.2s ease-in-out;
  }

  .bcpp-module-lessons {
    padding: 8px 20px 16px;
    background: var(--bs-white);
  }

  .bcpp-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    /* Span edge-to-edge: the box is the container's content width (100%) plus the 20px
       side padding on each side, bled outward with negative margins and restored as inner
       padding. An explicit width (not `auto`) is required because a <button> row would
       otherwise shrink-to-fit and its divider would stop short of the right edge — the
       <div> (locked) rows filled but the <button> rows didn't. box-sizing: border-box
       (global) makes this width include the padding. */
    width: calc(100% + 40px);
    text-align: left;
    background: none;
    border: none;
    margin: 0 -20px;
    padding: 12px 20px;
    cursor: pointer;

    /* Discreet divider between lessons to ease scanning; none after the last row. */
    &:not(:last-child) {
      border-bottom: 1px solid var(--bs-gray-200);
    }

    &.bcpp-item--clickable:hover .bcpp-item-title {
      text-decoration: underline;
    }

    &.bcpp-item--locked,
    &.bcpp-item--static {
      cursor: default;
    }
  }

  .bcpp-item-status {
    flex-shrink: 0;
    line-height: 1;
    padding-top: 2px;
  }

  .bcpp-item-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  .bcpp-item-icon--locked {
    width: 23px;
    height: 23px;
  }

  .bcpp-item-icon--in-progress {
    width: 22px;
    height: 22px;
    background-color: #FFC107;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 12px;
      color: white;
      /* bi-clock is a webfont glyph: font-weight does not thicken it.
         -webkit-text-stroke adds stroke weight so it matches the other
         icons (check, lock, etc.) visually. */
      -webkit-text-stroke: 0.6px white;
    }
  }

  .bcpp-item-icon--failed {
    opacity: 0.6;
  }

  .bcpp-item-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .bcpp-item-title {
    color: var(--bs-primary);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.28px;
    line-height: 1.3;
  }

  .bcpp-item--locked .bcpp-item-title {
    color: var(--bs-gray-600, #6C757D);
  }

  .bcpp-item-meta {
    color: var(--bs-body-color);
    font-size: 14px;
    letter-spacing: -0.28px;
    line-height: 1.3;
  }

  .bcpp-item--locked .bcpp-item-meta {
    color: var(--bs-gray-600, #6C757D);
  }

  .bcpp-item-locked-text {
    color: var(--bs-body-color, #212529);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.28px;
    line-height: 1.3;
  }

  .bcpp-item-locked-link {
    display: inline;
    background: none;
    border: none;
    padding: 0;
    color: var(--bs-primary, #5C0CB8);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.28px;
    line-height: 1.3;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:focus,
    &:focus-visible {
      outline: 2px solid var(--bs-primary);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
</style>
